/**
 * Merge entity-specific rows with global rows on a per-key basis.
 * Entity rows override globals with the same key; non-overridden globals are kept.
 * Result is sorted by sort_order.
 *
 * @param allRows        - All rows (both global and entity-specific)
 * @param entityId       - The current entity ID (null = use globals only)
 * @param keyFn          - Function to extract the merge key from a row (defaults to `r.value`)
 * @param suppressedIds  - Optional set of record IDs to suppress (filter out) from the final result
 */
export function mergeEntityRows<T extends { id?: string; company_entity_id?: string | null; sort_order?: number }>(
  allRows: T[],
  entityId: string | null | undefined,
  keyFn: (row: T) => string = (r: any) => r.value,
  suppressedIds?: Set<string>
): T[] {
  if (!entityId) {
    const result = allRows.filter((r) => r.company_entity_id == null);
    if (suppressedIds && suppressedIds.size > 0) {
      return result.filter((r) => !suppressedIds.has((r as any).id));
    }
    return result;
  }

  const entityRows = allRows.filter((r) => r.company_entity_id === entityId);
  const globalRows = allRows.filter((r) => r.company_entity_id == null);

  if (entityRows.length === 0) {
    const result = globalRows;
    if (suppressedIds && suppressedIds.size > 0) {
      return result.filter((r) => !suppressedIds.has((r as any).id));
    }
    return result;
  }

  // Entity keys that override globals
  const overriddenKeys = new Set(entityRows.map(keyFn));

  // Keep non-overridden globals + all entity rows, sorted by sort_order
  const merged = [
    ...globalRows.filter((r) => !overriddenKeys.has(keyFn(r))),
    ...entityRows,
  ];

  // Filter out suppressed records
  const filtered = suppressedIds && suppressedIds.size > 0
    ? merged.filter((r) => !suppressedIds.has((r as any).id))
    : merged;

  filtered.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  return filtered;
}
